import ChildHrefNavigationBlocker from "@/components/ChildHrefNavigationBlocker";
import Circular from "@/components/Circular";
import { ConditionalLink } from "@/components/ConditionalLink";
import { FlexColumn, FlexRowAlignCenter } from "@/components/Flex";
import { useSidebarContextSelector } from "@/components/NotePage/hooks/SidebarContext";
import StarRating from "@/components/StarRating";
import { ASSETS_URL } from "@/config/deployConstants";
import { clickReadonlyKaiChat } from "@/features/AI/KaiChat/ReadonlyChatInterface";
import { AuthEvent } from "@/features/Auth/AuthPopup/utils/constants";
import { useUpgradePopupContextSelector } from "@/features/Payments/PaywallPopup/UpgradePopupContext";
import { useAiPrivacyContextSelector } from "@/hooks/useAiPrivacyContextSelector";
import { useCheckAuth } from "@/hooks/useCheckAuth";
import Mixpanel from "@/utils/analytics/Mixpanel";
import { iconSizes, strokeWidth } from "@/utils/iconProps";
import { themeColors } from "@/utils/themeColors";
import { getPlainTextFromContent } from "@knowt/editor/helpers/getPlainTextFromContent";
import { Exam, FileType, ItemType, UpgradeEvent } from "@knowt/syncing/graphql/schema";
import { AP_EXAM_MCQ_MAP } from "@knowt/syncing/hooks/exams/constants";
import { examNameToUrlComponent, examTypeNameToUrlComponent } from "@knowt/syncing/hooks/exams/utils";
import { useFlashcardSet } from "@knowt/syncing/hooks/flashcards/useFlashcards";
import { useMedia } from "@knowt/syncing/hooks/media/useMedia";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import Box, { BoxProps } from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import { useRouter } from "@bprogress/next";
import toast from "react-hot-toast";
import { useNotePageContextSelector } from "../../hooks/NotePageContext";
import { getExamData } from "@/features/LandingPages/ExamPages/utils/fetch";
import { ClassDashboardTab } from "@/features/UserContentManagement/ClassPage/types";
import { useEffect, useState } from "react";
import { getDisplayNameFromFileType } from "@knowt/syncing/utils/fileTypeUtils";

export const SidebarBtn = ({
    label,
    href,
    sx,
    img,
    imgBg,
    onClick,
    misc,
    imgStyle,
    gradientBorder,
    ...props
}: {
    misc?: React.ReactNode;
    label: React.ReactNode;
    href?: string;
    img?: string;
    imgBg?: string;
    imgStyle?: React.CSSProperties;
    gradientBorder?: boolean;
} & BoxProps<"div">) => {
    return (
        <ConditionalLink condition={!onClick} href={href}>
            <Box
                sx={
                    gradientBorder
                        ? {
                              background: themeColors.aiGradientFlip,
                              borderRadius: "1.2rem",
                              padding: "1.5px",
                              "&:hover": { background: themeColors.ai },
                          }
                        : {}
                }>
                <Box
                    {...props}
                    onClick={onClick}
                    as={FlexRowAlignCenter}
                    className="secondaryTextBold1"
                    sx={{
                        padding: "1rem",
                        color: themeColors.neutralBlack,
                        gap: "1rem",
                        width: "100%",
                        borderRadius: "1rem",
                        "&:hover": !gradientBorder
                            ? { boxShadow: "none", outline: `1px solid ${themeColors.neutralBlack}` }
                            : {},
                        backgroundColor: gradientBorder ? themeColors.neutralWhite : "transparent",
                        cursor: "pointer",
                        fontSize: "1.4rem",
                        minHeight: "4rem",
                        ...sx,
                    }}>
                    {img && (
                        <Circular radius="4rem" style={{ borderRadius: "0.5rem", background: imgBg }}>
                            <Image
                                src={img}
                                width={24}
                                height={24}
                                style={{ objectFit: "contain", ...imgStyle }}
                                alt={"study"}
                            />
                        </Circular>
                    )}
                    <FlexColumn style={{ gap: "1rem", textWrap: "wrap" }}>
                        {label}
                        {misc}
                    </FlexColumn>
                    <Circular radius="2.4rem" style={{ background: themeColors.neutralBlack, marginLeft: "auto" }}>
                        <ArrowRight
                            strokeWidth={strokeWidth.normal}
                            size={iconSizes.SM}
                            color={themeColors.neutralWhite}
                        />
                    </Circular>
                </Box>
            </Box>
        </ConditionalLink>
    );
};

export const PracticeTestButton = () => {
    const { hasReachedAIUsage } = useCurrentUser();
    const { checkAuth } = useCheckAuth();
    const router = useRouter();

    const setIsAiPrivacyPopupOpen = useAiPrivacyContextSelector(context => context.setIsAiPrivacyPopupOpen);
    const shouldDisplayAiPrivacyPopup = useAiPrivacyContextSelector(context => context.shouldDisplayAiPrivacyPopup);
    const setOpenAiInputMenu = useAiPrivacyContextSelector(context => context.setOpenAiInputMenu);
    const openUpgradePopup = useUpgradePopupContextSelector(state => state.openUpgradePopup);
    const note = useNotePageContextSelector(state => state.note);
    const loading = useNotePageContextSelector(state => state.loading);

    const hasPracticeRoom = Object.keys(AP_EXAM_MCQ_MAP).includes(note.exam_v2 ?? "");

    const getExamRoute = (exam_v2: string | null | undefined) => {
        if (exam_v2) {
            const examType = exam_v2.split(" ")[0];
            return `/exams/${examTypeNameToUrlComponent(examType)}/${examNameToUrlComponent(
                exam_v2
            )}/practice-test-room`;
        }

        return null;
    };

    const examRoute = getExamRoute(note.exam_v2);

    return (
        <ChildHrefNavigationBlocker
            onClick={async () => {
                const isAuthenticated = await checkAuth({
                    event: AuthEvent.NOTE_EDITOR_STUDY_BUTTON,
                });

                if (!isAuthenticated) return;

                if (loading || !note) return;

                if (!getPlainTextFromContent({ content: note?.content || "", type: "note" }).trim())
                    return toast.error("Sorry, you don’t have a note to take a test on");

                if (examRoute && hasPracticeRoom) {
                    return router.push(examRoute);
                }

                if (shouldDisplayAiPrivacyPopup()) {
                    setIsAiPrivacyPopupOpen(true);
                    setOpenAiInputMenu(() => () => {
                        router.push(`/study/note/${note.noteId}/test`);
                    });
                    return;
                }

                if (hasReachedAIUsage("nTests")) {
                    return openUpgradePopup({
                        event: UpgradeEvent.MAX_NOTE_TESTS,
                        context: { itemId: note.noteId, itemType: ItemType.NOTE },
                    });
                }

                router.push(`/study/note/${note.noteId}/test`);
            }}>
            <SidebarBtn
                sx={{ border: "none", backgroundColor: themeColors.neutralWhite }}
                img={`${ASSETS_URL}/images/ai-summarizer-note-test.svg`}
                imgBg={themeColors.aiGradientFlip}
                label="Take a practice test"
                href={examRoute && hasPracticeRoom ? examRoute : `/study/note/${note.noteId}/test`}
                gradientBorder
            />
        </ChildHrefNavigationBlocker>
    );
};

export const FlashcardsButton = () => {
    const loading = useNotePageContextSelector(state => state.loading);
    const note = useNotePageContextSelector(state => state.note);
    const createFlashcardSet = useNotePageContextSelector(state => state.createFlashcardSet);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);

    const { flashcardSet } = useFlashcardSet({ flashcardSetId: note.flashcardSetId, raw: !isNoteOwner });

    if (!isNoteOwner && !note.flashcardSetId) return null;

    if (!note?.flashcardSetId && isNoteOwner) {
        return (
            <SidebarBtn
                img={`${ASSETS_URL}/images/ai-summarizer-flashcards.svg`}
                imgBg={themeColors.aiGradientFlip}
                label={"Generate flashcards"}
                onClick={() => {
                    if (loading) return;
                    Mixpanel.track("Note Flashcards Header Button - Clicked", { noteId: note.noteId });
                    createFlashcardSet();
                }}
                gradientBorder
            />
        );
    }

    return (
        <SidebarBtn
            style={{ border: `1px solid ${themeColors.neutral1}` }}
            img={`${ASSETS_URL}/images/ai-summarizer-flashcards.svg`}
            imgBg={themeColors.aiGradientFlip}
            label={isNoteOwner ? "View flashcards" : `${flashcardSet?.title} Flashcards`}
            onClick={
                flashcardSet?.trash ? () => toast.error("This set is in trash, restore it before opening.") : undefined
            }
            href={
                flashcardSet?.trash ? undefined : note.flashcardSetId ? `/flashcards/${note.flashcardSetId}` : undefined
            }
            misc={
                <FlexRowAlignCenter style={{ gap: "0.5rem", color: themeColors.neutralBlack }}>
                    <Circular
                        className="secondaryText2"
                        style={{
                            borderRadius: "1rem",
                            backgroundColor: themeColors.background,
                            padding: "0.8rem 1.2rem",
                            width: "fit-content",
                        }}>
                        {flashcardSet?.flashcards?.length || 0} Cards
                    </Circular>
                    <Circular
                        className="secondaryText2"
                        style={{
                            color: themeColors.neutralBlack,
                            borderRadius: "1rem",
                            backgroundColor: themeColors.background,
                            padding: "0.8rem 1.2rem",
                            width: "fit-content",
                            gap: "0.4rem",
                        }}>
                        <span className="secondaryText2">{(flashcardSet?.rating || 0).toFixed(1)}</span>
                        <StarRating max={1} readOnly sx={{ fontSize: "1.2rem" }} value={flashcardSet?.rating || 0} />
                        <span className="secondaryText2">({flashcardSet?.ratingCount || 0})</span>
                    </Circular>
                </FlexRowAlignCenter>
            }
        />
    );
};

export const MediaButton = () => {
    const note = useNotePageContextSelector(state => state.note);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);
    const noteMedia = useNotePageContextSelector(state => state.noteMedia);
    const toggleNoteChatSplit = useSidebarContextSelector(state => state.toggleNoteChatSplit);
    const { media, isVideo, isAudio, isPDF } = useMedia({ mediaId: note.mediaId });

    if (!note.mediaId || (!isNoteOwner && !noteMedia?.public)) return null;

    if (!noteMedia) {
        return <Skeleton variant="rectangular" width="100%" height="7rem" style={{ borderRadius: "2rem" }} />;
    }

    return (
        <SidebarBtn
            label={`View the linked ${getDisplayNameFromFileType(media?.fileType as FileType)}`}
            img={
                isVideo
                    ? `${ASSETS_URL}/images/video-icon.svg`
                    : isAudio
                      ? `${ASSETS_URL}/images/audio-icon.svg`
                      : `${ASSETS_URL}/images/pdf-icon.svg`
            }
            imgBg={isVideo || isPDF ? themeColors.videoLight : themeColors.primaryLight}
            onClick={() => {
                toggleNoteChatSplit({ isMediaTabOpen: true });
            }}
            style={{ border: `1px solid ${themeColors.neutral1}` }}
        />
    );
};

export const ChatButton = () => {
    const toggleNoteChatSplit = useSidebarContextSelector(state => state.toggleNoteChatSplit);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);

    return (
        <SidebarBtn
            id="notes-page-chat-kai-button"
            gradientBorder
            label={isNoteOwner ? "Ask Kai to edit" : "Chat with Kai"}
            img={`${ASSETS_URL}/images/ai-summarizer-kai.svg`}
            imgBg={themeColors.aiGradientFlip}
            onClick={() => {
                if (!isNoteOwner) {
                    clickReadonlyKaiChat();
                    return;
                }

                toggleNoteChatSplit();
            }}
        />
    );
};

export const UnitButton = () => {
    const note = useNotePageContextSelector(state => state.note);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);
    const [examData, setExamData] = useState<Exam | null>(null);

    const unitData = examData?.units?.find(unit => unit?.name === note.examUnit);

    const { flashcardSet: unitFlashcardSet } = useFlashcardSet({
        flashcardSetId: unitData?.flashcardSetId,
        raw: !isNoteOwner,
        isEnabled: !!unitData?.flashcardSetId,
    });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        async function fetchExamData() {
            if (!note.exam_v2 || !note.examUnit) return;

            try {
                setIsLoading(true);
                const data = await getExamData(note.exam_v2);
                if (data) setExamData(data);
            } finally {
                setIsLoading(false);
            }
        }

        fetchExamData();
    }, [note.exam_v2, note.examUnit]);

    if (!note.examUnit || !note.exam_v2 || isLoading || !examData) return null;

    return (
        <SidebarBtn
            style={{ border: `1px solid ${themeColors.neutral1}` }}
            label={note.examUnit}
            img={`${ASSETS_URL}/images/ai-summarizer-flashcards.svg`}
            imgBg={themeColors.aiGradientFlip}
            href={
                unitFlashcardSet?.flashcardSetId
                    ? `/flashcards/${unitFlashcardSet?.flashcardSetId}`
                    : `/exams/${examData?.type}/view/${examNameToUrlComponent(examData.name)}/${examNameToUrlComponent(
                          note.examUnit
                      )}-flashcards`
            }
            misc={
                unitFlashcardSet?.flashcardSetId && (
                    <FlexRowAlignCenter
                        style={{
                            gap: "0.5rem",
                            color: themeColors.neutralBlack,
                        }}>
                        <Circular
                            className="secondaryText2"
                            style={{
                                borderRadius: "1rem",
                                backgroundColor: themeColors.background,
                                padding: "0.8rem 1.2rem",
                                width: "fit-content",
                            }}>
                            {unitFlashcardSet?.flashcards?.length || 0} Cards
                        </Circular>
                        <Circular
                            className="secondaryText2"
                            style={{
                                color: themeColors.neutralBlack,
                                borderRadius: "1rem",
                                backgroundColor: themeColors.background,
                                padding: "0.8rem 1.2rem",
                                width: "fit-content",
                                gap: "0.4rem",
                            }}>
                            <span className="secondaryText2">{(unitFlashcardSet?.rating || 0).toFixed(1)}</span>
                            <StarRating
                                max={1}
                                readOnly
                                sx={{ fontSize: "1.2rem" }}
                                value={unitFlashcardSet?.rating || 0}
                            />
                            <span className="secondaryText2">({unitFlashcardSet?.ratingCount || 0})</span>
                        </Circular>
                    </FlexRowAlignCenter>
                )
            }
        />
    );
};

export const ClassPerformanceButton = () => {
    const note = useNotePageContextSelector(state => state.note);
    const isNoteOwner = useNotePageContextSelector(state => state.isNoteOwner);

    if (!isNoteOwner || !note.classId) return null;

    return (
        <SidebarBtn
            label="Check class performance"
            href={`/class/${note.classId}/dashboard?tab=${ClassDashboardTab.MATERIALS}&itemId=${note.noteId}&itemType=${ItemType.NOTE}`}
            style={{ border: `1px solid ${themeColors.neutral1}` }}
            imgBg={themeColors.neutral3}
            img={`${ASSETS_URL}/images/progress-icon-white.svg`}
            imgStyle={{
                height: "2rem",
            }}
        />
    );
};
